import { MenuItemDef } from '@ag-grid-community/core'
import { SourceKey } from '../models/source-key.model'
import { WorkbenchRow } from '../models/workbench-row.model'
import { scopeAllowsAction, scopeIsIgnorable } from './available-actions'

export interface ContextMenuDeps {
  /** The source the grid showing this menu is pointed at — decides which items are RENDERED. */
  sourceKey: SourceKey
  /** The rows the action applies to: the multi-row selection, or the single right-clicked row. */
  scopeRows: WorkbenchRow[]
  /**
   * Whether the live cross-grid selection spans >= 2 distinct grid sources
   * (`WorkbenchStoreService.selectionSpansSources`). The UI-only half of the Match gate: only the
   * client knows the live selection, so the backend's per-row `availableActions` cannot express it.
   */
  selectionSpansSources: boolean
  /**
   * Whether this click would ADD to an existing group rather than create one — the Match Additional
   * label. See {@link matchAdditionalApplies}, which is where the design's rule lives; the grid
   * computes it because only the store knows the sibling grid's live selection.
   */
  matchAdditional: boolean
  onMatch: () => void
  /**
   * The scope ROWS, not their ids: the dispatch has to split them by feed to build the request's
   * `enfnPosIds` / `promPosIds`, and only the row knows its origin.
   */
  onUnmatch: (rows: WorkbenchRow[]) => void
  onIgnore: (rows: WorkbenchRow[], value: boolean) => void
  highlightItems: (string | MenuItemDef)[]
  filterOnCell: (string | MenuItemDef)[]
}

type MenuItem = string | MenuItemDef

const iconOf = (iconName?: string): Pick<MenuItemDef, 'icon'> =>
  iconName ? { icon: `<i class="mdi ${iconName}"></i>` } : {}

/**
 * An item that renders and reads as enabled but does nothing yet.
 *
 * It has to be a `MenuItemDef` rather than a plain label: ag-Grid accepts a bare string ONLY when it
 * names one of its own built-in items (`separator`, `copy`, `export`, …) and silently DROPS anything
 * else, so a custom label written as a string never reaches the screen. `action` is an explicit
 * no-op so the item is clickable rather than greyed out, matching the design.
 */
const placeholder = (name: string, iconName?: string): MenuItemDef => ({
  name,
  ...iconOf(iconName),
  action: () => undefined,
})

/** Transfer Position sub-items, per the design's Clearwater action set. */
const TRANSFER_TYPES = ['Direct', 'Security Transfer Out']

/**
 * The items every source shows below its matching block: Find in External File Viewer (Clearwater
 * and Prometheus only), the shared filter-on-cell group, and the highlight group.
 */
const commentItems = (): MenuItemDef[] => [
  placeholder('View Comments', 'mdi-comment-outline'),
  placeholder('Add Comment', 'mdi-comment-plus-outline'),
]

/**
 * Build the source-specific right-click menu for a positions workbench grid, mirroring
 * transactions-2's `utils/context-menu.ts`.
 *
 * Two layers of enablement, exactly as PVAL-26403 splits them:
 *  - **Per row** — the backend's `availableActions` (see `utils/available-actions.ts`) says which
 *    actions the row is eligible for, plus the local gates the engine itself enforces
 *    (Ignore/Unignore are Prometheus-origin only; Unmatch needs a group).
 *  - **Per selection** — Match additionally needs the live selection to span >= 2 distinct grid
 *    sources, which is a client-only fact.
 *
 * Neither layer is a security boundary: the `/flex-positions/actions` endpoint re-validates on
 * submit. These checks decide what is *enabled* and which modal opens.
 *
 * Items are DISABLED rather than omitted when a row is ineligible (transactions-2's convention, and
 * ag-Grid's): the menu's shape stays stable between rows, so a user learns where an action lives
 * instead of watching items appear and vanish. "An ignored Prometheus row shows only Unignore"
 * therefore means Unignore is the only *enabled* item on it.
 */
export function buildContextMenuItems(deps: ContextMenuDeps): MenuItem[] {
  const { scopeRows } = deps
  const cnt = scopeRows.length > 1 ? ` ${scopeRows.length}` : ''

  // Whether any scoped row is grouped — the Unmatch gate. NOT the Match Additional rule, which is
  // narrower; see `matchAdditionalApplies`.
  const isMatched = scopeRows.some(row => !!row.group)

  const matchItem: MenuItemDef = {
    name: deps.matchAdditional ? 'Match Additional' : 'Match',
    disabled:
      !deps.selectionSpansSources || !scopeAllowsAction(scopeRows, 'match'),
    icon: '<i class="mdi mdi-link"></i>',
    action: () => deps.onMatch(),
  }

  const unmatchItem: MenuItemDef = {
    name: `Unmatch${cnt}`,
    disabled: !isMatched || !scopeAllowsAction(scopeRows, 'unmatch'),
    icon: '<i class="mdi mdi-link-off"></i>',
    action: () => deps.onUnmatch(scopeRows),
  }

  const matchingItems: MenuItem[] = [matchItem, unmatchItem]

  // Enfusion is the raw feed: matching, Filter On Cell and Highlight only. No comments, ignore,
  // transfer or security mapping — mirroring how Transactions 2.0 treats its API source
  // (Positions-2.0-Feature-Differences.md §4).
  if (deps.sourceKey === 'api') {
    return [
      ...matchingItems,
      'separator',
      ...deps.filterOnCell,
      'separator',
      ...deps.highlightItems,
    ]
  }

  // Clearwater: no Ignore (a Prometheus-only concept) but it owns the position-movement actions.
  if (deps.sourceKey === 'cw') {
    return [
      ...matchingItems,
      'separator',
      {
        name: 'Transfer Position',
        ...iconOf('mdi-bank-transfer'),
        subMenu: TRANSFER_TYPES.map<MenuItemDef>(label => ({
          name: label,
          action: () => undefined,
        })),
      },
      placeholder('Exchange', 'mdi-swap-horizontal'),
      'separator',
      placeholder('Find in External File Viewer', 'mdi-folder-open'),
      ...deps.filterOnCell,
      'separator',
      ...deps.highlightItems,
      'separator',
      ...commentItems(),
    ]
  }

  // Prometheus: the external feed — ignoring, security mapping and comments all apply.
  //
  // Ignore lives HERE because the design says so: the action matrix in
  // Positions-2.0-Feature-Differences.md §4 marks Ignore/Unignore Prometheus-only, and the
  // prototype's own `setIgnore` refuses any row whose `src` is not `cust`/`trad`
  // (`Positions 2.0.dc.html:1072`), with the pair rendered in this branch at `:1694-1695`.
  //
  // ⚠ The actions endpoint does NOT agree yet — see `utils/action-request.ts` for what that costs.
  return [
    ...matchingItems,
    'separator',
    ...ignoreItems(deps, cnt),
    'separator',
    placeholder('Create Security Mapping', 'mdi-sitemap'),
    placeholder('Find in External File Viewer', 'mdi-folder-open'),
    ...deps.filterOnCell,
    'separator',
    ...deps.highlightItems,
    'separator',
    ...commentItems(),
  ]
}

/**
 * The Ignore / Unignore pair, which only the Prometheus menu renders.
 *
 * Both are gated on {@link scopeIsIgnorable} (the local origin rule) AND on `availableActions` —
 * either alone is enough to disable the item. The already-ignored / not-yet-ignored halves are the
 * obvious part: a row cannot be ignored twice, or unignored when it was never ignored.
 *
 * What is deliberately NOT gated here is the position type. The endpoint refuses a cash balance
 * (`unsupportedPositionType`), but `WorkbenchRow.sourceLabel` is one of the promoted properties the
 * row adapter leaves undefined — see its KNOWN GAP note — so gating on it would disable Ignore for
 * every real row while only ever working in fixtures. The 400 is surfaced instead.
 */
function ignoreItems(deps: ContextMenuDeps, cnt: string): MenuItemDef[] {
  const { scopeRows } = deps
  const ignorable = scopeIsIgnorable(scopeRows)
  const allIgnored = scopeRows.length > 0 && scopeRows.every(row => row.ignored)
  const noneIgnored = scopeRows.every(row => !row.ignored)

  return [
    {
      name: `Ignore${cnt}`,
      disabled:
        !ignorable || allIgnored || !scopeAllowsAction(scopeRows, 'ignore'),
      icon: '<i class="mdi mdi-cancel"></i>',
      action: () => deps.onIgnore(scopeRows, true),
    },
    {
      name: `Unignore${cnt}`,
      disabled:
        !ignorable || noneIgnored || !scopeAllowsAction(scopeRows, 'unignore'),
      icon: '<i class="mdi mdi-cancel"></i>',
      action: () => deps.onIgnore(scopeRows, false),
    },
  ]
}
